import React, { useState } from "react";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Label,
  Input,
  Spinner,
  Form,
  Alert,
} from "reactstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { bulkResolveContractorDuplicates } from "common/services/contractors.service";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useFormik } from "formik";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const BulkResolveContractorDuplicates: React.FC<{
  toggle: () => void;
  isModalOpen: boolean;
  selectedIds: Array<string | number>;
  fetchData: () => void;
}> = ({ toggle, isModalOpen, selectedIds, fetchData }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      comments: "",
      isConfirmedDuplicate: false,
    },
    validationSchema: Yup.object({
      comments: Yup.string().nullable(),
      isConfirmedDuplicate: Yup.boolean(),
    }),
    onSubmit: async (values: any) => {
      setLoading(true);
      try {
        await bulkResolveContractorDuplicates({
          ids: selectedIds,
          status: values.isConfirmedDuplicate ? "duplicate" : "not_duplicate",
          note: values.comments,
        });
        fetchData();
        validation.resetForm();
        toggle();
        toast.success("Contractor duplicates resolved successfully", {});
      } catch (e: any) {
        toast.error("Bulk resolution failed", {});
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Modal id="bulkResolveModal" isOpen={isModalOpen} toggle={toggle} centered>
      <ModalHeader className="bg-primary-subtle p-3" toggle={toggle}>
        Bulk Resolve Duplicates
      </ModalHeader>

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <ModalBody>
          <Alert color="warning" className="material-shadow">
            You are about to resolve <strong>{selectedIds.length}</strong>{" "}
            selected duplicate record{selectedIds.length === 1 ? "" : "s"}{" "}
            with the same status and comment.
          </Alert>
          <Row>
            <Col md={12} className="mb-4">
              <div className="form-check form-switch form-switch-lg px-0 form-check-right">
                <Input
                  name="isConfirmedDuplicate"
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="bulkIsConfirmedDuplicate"
                  value={validation.values.isConfirmedDuplicate}
                  onChange={validation.handleChange}
                  checked={validation.values.isConfirmedDuplicate}
                />
                <Label
                  className="form-check-label"
                  htmlFor="bulkIsConfirmedDuplicate"
                  style={{ width: "250px" }}
                >
                  Are these confirmed duplicate contractors?
                </Label>
              </div>
            </Col>
            <Col md={12} className="mb-3">
              <Label htmlFor="id-field" className="form-label">
                Comments on resolution (Optional)
              </Label>
              <CKEditor
                editor={ClassicEditor}
                data={validation.values.comments || ""}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  validation.setFieldValue("comments", data);
                }}
                onBlur={() => {
                  validation.handleBlur("comments");
                }}
              />
            </Col>
          </Row>
        </ModalBody>

        <div className="modal-footer d-flex justify-content-end">
          <div className="hstack gap-2">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => {
                toggle();
              }}
              disabled={loading}
            >
              Close
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || selectedIds.length === 0}
            >
              {loading && (
                <Spinner
                  color="light"
                  size="sm"
                  style={{ marginRight: "8px", marginBottom: "-1px" }}
                ></Spinner>
              )}
              Resolve {selectedIds.length} Duplicate
              {selectedIds.length === 1 ? "" : "s"}
            </button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default BulkResolveContractorDuplicates;
